import { useEffect } from 'react'
import TopSafeSpacer from '../layout/TopSafeSpacer'
import { useNavigate } from 'react-router-dom'
import { useAdmin } from '../../hooks/useAdmin'

export default function Admin() {
  const navigate = useNavigate()
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null
  const { isAdmin, loading, role, permissions } = useAdmin()

  useEffect(() => {
    if (!tg) return

    // Show back button
    try {
      const handleBack = () => navigate('/')
      tg.BackButton?.show()
      tg.BackButton?.onClick(handleBack)

      return () => {
        tg.BackButton?.offClick(handleBack)
      }
    } catch (error) {
      console.warn('Failed to setup back button:', error)
    }

    // Hide main button
    try {
      tg.MainButton?.hide()
    } catch (error) {
      console.warn('Failed to hide main button:', error)
    }
  }, [tg])

  // Redirect if not admin
  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/')
    }
  }, [loading, isAdmin, navigate])

  if (loading) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Checking permissions...</span>
      </div>
    )
  }

  if (!isAdmin) {
    return null // Will redirect
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Top padding for Telegram UI */}
      <TopSafeSpacer />

      {/* Admin Header */}
      <header className="bg-blue-600 text-white px-4 py-3 relative">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Admin Panel</h1>
          <div className="bg-blue-700 px-2 py-1 rounded text-xs font-semibold">
            {role.toUpperCase()}
          </div>
        </div>
        <div className="absolute top-0 right-0 bg-red-500 text-white text-xs px-2 py-1">
          ADMIN MODE
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        <div className="max-w-md mx-auto">
          {/* Admin Info */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-blue-200 mb-6">
            <h3 className="font-semibold text-gray-800 mb-2">Admin Information</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Role:</span>
                <span className="font-semibold text-blue-600">{role}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Permissions:</span>
                <span className="font-semibold">{permissions.length}</span>
              </div>
            </div>
            <div className="mt-3">
              <p className="text-xs text-gray-500">Active permissions:</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {permissions.map(permission => (
                  <span
                    key={permission}
                    className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded"
                  >
                    {permission.replace('_', ' ')}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Admin Features */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => tg?.showAlert('Product management coming soon!')}
              className="bg-white p-4 rounded-xl shadow-sm border border-blue-200 text-center active:scale-95 transition-transform"
            >
              <div className="text-2xl mb-2">📦</div>
              <h3 className="font-semibold text-sm">Products</h3>
              <p className="text-xs text-gray-500">Manage catalog</p>
            </button>

            <button
              onClick={() => tg?.showAlert('Order management coming soon!')}
              className="bg-white p-4 rounded-xl shadow-sm border border-blue-200 text-center active:scale-95 transition-transform"
            >
              <div className="text-2xl mb-2">📋</div>
              <h3 className="font-semibold text-sm">Orders</h3>
              <p className="text-xs text-gray-500">Track orders</p>
            </button>

            <button
              onClick={() => tg?.showAlert('Analytics dashboard coming soon!')}
              className="bg-white p-4 rounded-xl shadow-sm border border-blue-200 text-center active:scale-95 transition-transform"
            >
              <div className="text-2xl mb-2">📊</div>
              <h3 className="font-semibold text-sm">Analytics</h3>
              <p className="text-xs text-gray-500">View metrics</p>
            </button>

            <button
              onClick={() => tg?.showAlert('User management coming soon!')}
              className="bg-white p-4 rounded-xl shadow-sm border border-blue-200 text-center active:scale-95 transition-transform"
            >
              <div className="text-2xl mb-2">👥</div>
              <h3 className="font-semibold text-sm">Users</h3>
              <p className="text-xs text-gray-500">Manage users</p>
            </button>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-blue-200 mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/products')}
                className="w-full bg-blue-500 text-white py-3 rounded-lg text-sm font-medium active:bg-blue-600 transition-colors"
              >
                View Store (Customer View)
              </button>
              <button
                onClick={() => tg?.showAlert('System settings coming soon!')}
                className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg text-sm font-medium active:bg-gray-200 transition-colors"
              >
                System Settings
              </button>
            </div>
          </div>

          {/* Debug Info */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="text-yellow-700 text-sm mb-2">
              🔧 Debug Information
            </div>
            <div className="text-xs text-yellow-600">
              Admin panel is currently in demo mode. Real admin functionality will be implemented in production.
            </div>
          </div>
        </div>
      </main>

      {/* Bottom Safe Area */}
      <div className="safe-bottom"></div>
    </div>
  )
}